<script setup lang="ts">
import { Copy, WarningTriangle } from "@iconoir/vue";

defineOptions({ name: "AppSecretModal" });

const { t } = useI18n();
const toast = useToast();

const isOpen = ref(false);
const clientSecret = ref("");
const signingSecret = ref("");

function open(secrets: { clientSecret: string; signingSecret: string }) {
  clientSecret.value = secrets.clientSecret;
  signingSecret.value = secrets.signingSecret;
  isOpen.value = true;
}

function close() {
  isOpen.value = false;
  clientSecret.value = "";
  signingSecret.value = "";
}

async function copy(value: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success({ title: t("app.secret.copied") });
  } catch {
    toast.error({ title: t("app.secret.copyFailed") });
  }
}

defineExpose({ open });
</script>

<template>
  <!-- One-time reveal: the client secret is stored hashed and can never be shown again. -->
  <Modal
    :open="isOpen"
    :title="t('app.secret.title')"
    size="md"
    :closable="false"
    @close="close"
  >
    <template #default>
      <div class="app-secret flex flex-col">
        <div class="app-secret__warning">
          <WarningTriangle class="icon" />
          <div>
            <p class="app-secret__warning-title text-body-small-secondary">
              {{ t("app.secret.warningTitle") }}
            </p>
            <p class="text-body-small-tertiary content-neutral-medium">
              {{ t("app.secret.warningDescription") }}
            </p>
          </div>
        </div>

        <div class="flex flex-col gap-xsmall">
          <span class="text-body-small-secondary">{{ t("app.secret.clientSecret") }}</span>
          <div class="app-secret__value">
            <code>{{ clientSecret }}</code>
            <Button variant="tertiary" size="small" @click="copy(clientSecret)">
              <Copy class="icon" />
              {{ t("app.secret.copy") }}
            </Button>
          </div>
        </div>

        <div class="flex flex-col gap-xsmall">
          <span class="text-body-small-secondary">{{ t("app.secret.signingSecret") }}</span>
          <!-- One secret signs both directions the platform sends; say so, the
               label alone no longer does. -->
          <span class="text-body-small-tertiary content-neutral-medium">{{ t("app.secret.signingSecretNote") }}</span>
          <div class="app-secret__value">
            <code>{{ signingSecret }}</code>
            <Button variant="tertiary" size="small" @click="copy(signingSecret)">
              <Copy class="icon" />
              {{ t("app.secret.copy") }}
            </Button>
          </div>
        </div>
      </div>
    </template>

    <template #actions>
      <Button variant="primary" size="medium" @click="close">
        {{ t("app.secret.dismiss") }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.app-secret {
  gap: token("gap-medium");

  &__warning {
    display: flex;
    gap: token("gap-small");
    padding: token("padding-medium");
    background: token("color-bg-canvas");
    border: 1px solid token("color-border-warning-default");
    border-radius: token("corner-radius-small");

    .icon {
      color: token("color-content-warning-default");
      flex-shrink: 0;
    }
  }

  &__warning-title {
    margin: 0 0 token("gap-3xsmall");
  }

  &__value {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: token("gap-small");
    padding: token("padding-medium");
    background: token("color-bg-canvas");
    border: 1px solid token("color-border-neutral-alpha-light-default");
    border-radius: token("corner-radius-small");

    code {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 0.9em;
      word-break: break-all;
    }
  }
}
</style>
